"use client";

import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import { Logger } from "@/utils/logger";

import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from "./session-storage";
import { StorageValue } from "./types";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "./use-local-storage";

export type StorageType = "local" | "session";
const parse = <T>(raw: string | null, fallback: T): T => {
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    Logger.error(err, { raw });
    return fallback;
  }
};

const read = <T>(key: string, type: StorageType, fallback: T): T =>
  parse(type === "local" ? getLocalStorageItem(key) : getSessionStorageItem(key), fallback);

const write = (key: string, value: StorageValue, type: StorageType) => {
  (type === "local" ? setLocalStorageItem : setSessionStorageItem)(key, value);
};

const clearStorage = (key: string, type: StorageType) => {
  (type === "local" ? removeLocalStorageItem : removeSessionStorageItem)(key);
};

export function useStorage<T extends StorageValue>(
  key: string,
  initialValue: T,
  storageType: StorageType,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    // read both → prefer active
    const active = read(key, storageType, initialValue);

    if (active !== initialValue) return active;

    const fallback = read(key, storageType === "local" ? "session" : "local", initialValue);

    return fallback;
  });

  /**
   * Migration on storageType change
   */
  useEffect(() => {
    write(key, state, storageType);
    if (storageType === "session") {
      clearStorage(key, "local"); // clear the local storage, session automatically clears after the session is complete
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, storageType]);

  /**
   * Cross-tab sync
   */
  useEffect(() => {
    if (storageType === "session") return; // we don't care about session storage changes

    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;

      setState(parse<T>(e.newValue, initialValue));
    };

    window.addEventListener("storage", handler);

    return () => window.removeEventListener("storage", handler);
  }, [key, storageType, initialValue]);

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (value) => {
      // If value is a function, call it with state
      const newValue = typeof value === "function" ? (value as (prevState: T) => T)(state) : value;
      write(key, newValue, storageType);
      setState(newValue);
    },
    [key, storageType, state],
  );

  return [state, setValue];
}
